import React, { useState, useEffect } from "react";
import { Button } from "../../../../ui/components/Button";
import { Select } from "../../../../ui/components/Select";
import { TextField } from "../../../../ui/components/TextField";
import { TextArea } from "../../../../ui/components/TextArea";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { Loader } from "../../../../ui/components/Loader";
import { useToast } from "../../../../shared/presentation/components/Toast/ToastContext";
import {
  CompanyContextData,
  getCompanyContext,
  saveCompanyContext,
  generateCompanyContext,
} from "../../../../services/contextApi";

const COMPANY_TYPES = [
  "Pvt Ltd",
  "LLP",
  "Partnership",
  "Sole Proprietorship",
  "Public Ltd",
  "OPC",
  "Trust",
  "Society",
  "Other",
];

export const CompanyContextPage: React.FC = () => {
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [formData, setFormData] = useState<CompanyContextData>({
    legal_name: "",
    trading_name: "",
    company_type: "",
    website: "",
    core_business: "",
    revenue_streams: "",
    expense_categories: "",
    raw_input: "",
    final_context: "",
    status: "draft",
  });

  useEffect(() => {
    loadContext();
  }, []);

  const loadContext = async () => {
    try {
      const response = await getCompanyContext();
      if (response.success && response.data) {
        setFormData((prev) => ({ ...prev, ...response.data }));
      }
    } catch (error: any) {
      console.error("Failed to load company context:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (
    field: keyof CompanyContextData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    if (!formData.legal_name?.trim()) {
      showToast("Legal Name is required", "error");
      return;
    }
    if (!formData.core_business?.trim()) {
      showToast("Core Business is required", "error");
      return;
    }
    if (!formData.revenue_streams?.trim()) {
      showToast("Revenue Streams is required", "error");
      return;
    }
    setIsGenerating(true);
    try {
      const response = await generateCompanyContext({
        legal_name: formData.legal_name,
        trading_name: formData.trading_name,
        company_type: formData.company_type,
        core_business: formData.core_business,
        revenue_streams: formData.revenue_streams,
        expense_categories: formData.expense_categories,
        raw_input: formData.raw_input,
      });
      if (response.success && response.data?.ai_rewritten_context) {
        setFormData((prev) => ({
          ...prev,
          ai_rewritten_context: response.data.ai_rewritten_context,
          final_context: response.data.ai_rewritten_context,
        }));
        showToast("AI context generated successfully", "success");
      }
    } catch (error: any) {
      showToast(error?.message || "Failed to generate context", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await saveCompanyContext(formData);
      if (response.success) {
        setFormData((prev) => ({ ...prev, ...response.data }));
        showToast("Company context saved and active", "success");
      }
    } catch (error: any) {
      showToast(error?.message || "Failed to save context", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const charCount = formData.final_context?.length || 0;

  if (isLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader />
            <span className="text-body font-body text-subtext-color">
              Loading company context...
            </span>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border px-6 py-6">
          <div className="flex items-center gap-4">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Company Context for GL Classification
            </span>
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                formData.status === "confirmed"
                  ? "bg-success-100 text-success-700"
                  : "bg-neutral-100 text-neutral-600"
              }`}
            >
              {formData.status === "confirmed" ? "Active" : "Draft"}
            </span>
          </div>
          <Button onClick={handleSave} disabled={isSaving} loading={isSaving}>
            {isSaving ? "Saving..." : "Save & Confirm"}
          </Button>
        </div>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-auto">
          <div className="flex w-full flex-col items-start gap-8 px-6 py-6">
            {/* Company Information */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Company Information
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <TextField label="Legal Name">
                  <TextField.Input
                    placeholder="e.g. ABC Technologies Pvt Ltd"
                    value={formData.legal_name || ""}
                    onChange={(e) =>
                      handleFieldChange("legal_name", e.target.value)
                    }
                  />
                </TextField>
                <TextField label="Trading Name">
                  <TextField.Input
                    placeholder="e.g. ABC Tech"
                    value={formData.trading_name || ""}
                    onChange={(e) =>
                      handleFieldChange("trading_name", e.target.value)
                    }
                  />
                </TextField>
              </div>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <Select
                  label="Company Type"
                  placeholder="Select company type"
                  value={formData.company_type || ""}
                  onValueChange={(value) =>
                    handleFieldChange("company_type", value)
                  }
                >
                  {COMPANY_TYPES.map((type) => (
                    <Select.Item key={type} value={type}>
                      {type}
                    </Select.Item>
                  ))}
                </Select>
                <TextField label="Website">
                  <TextField.Input
                    placeholder="e.g. www.abctech.com"
                    value={formData.website || ""}
                    onChange={(e) =>
                      handleFieldChange("website", e.target.value)
                    }
                  />
                </TextField>
              </div>
            </div>

            {/* Business Description */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Business Description
              </span>
              <TextArea className="w-full" label="Core Business">
                <TextArea.Input
                  placeholder="Briefly describe what the company does..."
                  value={formData.core_business || ""}
                  onChange={(e) =>
                    handleFieldChange("core_business", e.target.value)
                  }
                />
              </TextArea>
              <TextArea className="w-full" label="Revenue Streams">
                <TextArea.Input
                  placeholder="Main sources of revenue..."
                  value={formData.revenue_streams || ""}
                  onChange={(e) =>
                    handleFieldChange("revenue_streams", e.target.value)
                  }
                />
              </TextArea>
              <TextArea className="w-full" label="Typical Expense Categories">
                <TextArea.Input
                  placeholder="Common expense categories..."
                  value={formData.expense_categories || ""}
                  onChange={(e) =>
                    handleFieldChange("expense_categories", e.target.value)
                  }
                />
              </TextArea>
            </div>

            {/* Additional Notes */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Additional Notes
              </span>
              <TextArea className="w-full" label="Any other information for the AI">
                <TextArea.Input
                  placeholder="Any additional context that would help the AI classify transactions..."
                  value={formData.raw_input || ""}
                  onChange={(e) =>
                    handleFieldChange("raw_input", e.target.value)
                  }
                />
              </TextArea>
            </div>

            {/* Generate Button */}
            <Button
              variant="neutral-secondary"
              onClick={handleGenerate}
              disabled={isGenerating}
              loading={isGenerating}
            >
              {isGenerating ? "Generating..." : "Generate AI Context"}
            </Button>

            {/* Final Context */}
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full items-center justify-between">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Final Context
                </span>
                <span
                  className={`text-caption font-caption ${
                    charCount > 750 ? "text-error-600" : "text-subtext-color"
                  }`}
                >
                  {charCount}/750
                </span>
              </div>
              <p className="text-body font-body text-subtext-color">
                This is the text that will be sent to the AI during GL
                classification. You can edit it after generation.
              </p>
              <TextArea className="w-full">
                <TextArea.Input
                  value={formData.final_context || ""}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 750);
                    handleFieldChange("final_context", value);
                  }}
                  placeholder="Click 'Generate AI Context' above, or type your own context here..."
                />
              </TextArea>
            </div>
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};

export default CompanyContextPage;
